/* Document Analysis Panel — sits at the bottom of the Review screen.
 * Lets underwriters upload a fresh document straight from their computer and
 * run AI extraction on it without first attaching it to the application via
 * the regular document slots.  Tool type is auto-detected from the file name
 * (Bank Statement Analysis vs Tax Return Analysis).  A checkbox controls
 * whether the uploaded file should also be saved to the application's
 * documents.  Results land in the Analysis & Reports section above.
 *
 * Visible to Admin / Underwriter / Jr Underwriter only.
 */
import React, { useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Sparkles, FileText, Loader2, AlertCircle, CheckCircle, Upload, X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const inferToolType = (filename) => {
  if (!filename) return null;
  const n = filename.toLowerCase();
  const bankHints = [
    'bank', 'deposit', 'statement', 'chase', 'bofa', 'boa',
    'wells', 'fargo', 'citi', 'ally', 'amex', 'capital one',
    'checking', 'savings', 'transactions',
  ];
  const taxHints = [
    'tax return', 'tax-return', 'taxreturn', '1120', '1065',
    '1040', 'k-1', 'k1', 'schedule c', 'schedule-c', 'schedulec',
    'p&l', 'pnl', 'p_l', 'profit', 'loss', 'income statement',
    'form 1120', 'form 1065', 'form 1040',
  ];
  if (bankHints.some((h) => n.includes(h))) return 'bank_statement_analysis';
  if (taxHints.some((h) => n.includes(h))) return 'tax_return_analysis';
  return null;
};

const labelForTool = (t) => (
  t === 'bank_statement_analysis' ? 'Bank Statement Analysis'
    : t === 'tax_return_analysis' ? 'Tax Return Analysis'
    : null
);

const DocumentAnalysisPanel = ({ application, authToken, onComplete }) => {
  const [file, setFile] = useState(null);
  const [saveToApp, setSaveToApp] = useState(true);
  const [running, setRunning] = useState(false);
  const [feedback, setFeedback] = useState(null); // {type:'ok'|'err', text:string}
  const inputRef = useRef(null);

  const inferred = useMemo(() => inferToolType(file?.name || ''), [file]);
  const toolLabel = labelForTool(inferred);

  const reset = () => {
    setFile(null);
    setFeedback(null);
    if (inputRef.current) inputRef.current.value = '';
  };

  const handleRun = async () => {
    if (!file) {
      setFeedback({ type: 'err', text: 'Pick a file to upload first.' });
      return;
    }
    if (!inferred) {
      setFeedback({
        type: 'err',
        text: 'Could not detect analysis type from the filename. Rename your file to include something like "tax-return", "1120", "1040", "P&L", or "bank-statement"/"deposits" and try again.',
      });
      return;
    }
    setRunning(true);
    setFeedback(null);

    const filenameSnapshot = file.name;
    const submittedAt = Date.now();

    // Record the snapshot of toolkit_results IDs BEFORE the call so we can
    // detect if the backend saved a result even when the HTTP response is
    // lost (production proxy timeout, network drop, etc).
    const beforeIds = new Set(
      (application.toolkit_results || []).map((r) => r?.id).filter(Boolean)
    );

    const checkBackendSavedResult = async () => {
      try {
        const { data: fresh } = await axios.get(
          `${BACKEND_URL}/api/applications/${application.id}`,
          { headers: { Authorization: `Bearer ${authToken}` } }
        );
        const results = fresh?.toolkit_results || [];
        // Match by filename + freshness (saved after we submitted).
        const match = results.find((r) => {
          if (beforeIds.has(r?.id)) return false;
          const savedFn = r?.result_data?.filename;
          const savedAt = r?.saved_at ? Date.parse(r.saved_at) : 0;
          return savedFn === filenameSnapshot && savedAt >= submittedAt - 5000;
        });
        return match || null;
      } catch (_e) {
        return null;
      }
    };

    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('application_id', application.id);
      fd.append('save_to_application', saveToApp ? 'true' : 'false');
      const resp = await axios.post(
        `${BACKEND_URL}/api/toolkit/upload-and-analyze`,
        fd,
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
            'Content-Type': 'multipart/form-data',
          },
          timeout: 180000,
        }
      );
      const label = labelForTool(resp.data?.tool_type || inferred) || 'Analysis';
      const savedMsg = resp.data?.saved_to_application
        ? ' File also saved under "AI Analysis Upload" in the Documents section.'
        : '';
      setFeedback({
        type: 'ok',
        text: `${label} complete.${savedMsg} Scroll up to "Analysis & Reports" to review the extracted data.`,
      });
      reset();
      if (onComplete) onComplete(resp.data);
    } catch (err) {
      // Production proxies sometimes drop the long-lived response while the
      // backend keeps running and persists the result successfully.  Before
      // declaring failure, poll the application for ~25 seconds to see if a
      // matching toolkit_result actually got saved.
      let saved = null;
      for (let i = 0; i < 8 && !saved; i += 1) {
        // Wait progressively longer between checks.
        // 3s, 4s, 5s, 5s, 5s, 5s, 5s, 5s = ~37s total worst-case
        // eslint-disable-next-line no-await-in-loop
        await new Promise((r) => setTimeout(r, i < 2 ? 3000 : 5000));
        // eslint-disable-next-line no-await-in-loop
        saved = await checkBackendSavedResult();
      }
      if (saved) {
        const label = labelForTool(saved.tool_type || inferred) || 'Analysis';
        setFeedback({
          type: 'ok',
          text: `${label} complete — the upload took longer than usual but finished successfully on the server. Scroll up to "Analysis & Reports" to review the extracted data.`,
        });
        reset();
        if (onComplete) onComplete({ saved_id: saved.id, tool_type: saved.tool_type });
      } else {
        const status = err?.response?.status;
        const detail = err?.response?.data?.detail || err?.response?.data?.message;
        const errCode = err?.code || '';
        const netHint = (errCode === 'ECONNABORTED') ? ' (request timed out)'
          : (errCode === 'ERR_NETWORK') ? ' (network error — gateway / CORS / DNS)'
          : '';
        const base = detail || 'Analysis failed. Try a different file or check the server logs.';
        setFeedback({
          type: 'err',
          text: `${base}\n\n[diagnostic — HTTP ${status || 'no-response'}${netHint}${err?.message ? ` — ${err.message}` : ''}]`,
        });
      }
    } finally {
      setRunning(false);
    }
  };

  if (!application?.id) return null;

  return (
    <Card className="lg:col-span-3 border-cyan-200 bg-cyan-50/30" data-testid="document-analysis-panel">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-cyan-700" />
          Upload &amp; Analyze a Document
        </CardTitle>
        <p className="text-xs text-slate-600 mt-1">
          Upload any tax return, P&amp;L, or bank statement straight from your computer.
          The analysis type is auto-detected from the file name and results are saved
          into <strong>Analysis &amp; Reports</strong> above.
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        <div>
          <Label className="text-[11px] uppercase tracking-wide text-slate-500">Document</Label>

          <input
            ref={inputRef}
            type="file"
            accept=".pdf,.png,.jpg,.jpeg,.webp"
            className="hidden"
            data-testid="dap-file-input"
            onChange={(e) => {
              const f = e.target.files?.[0] || null;
              setFile(f);
              setFeedback(null);
            }}
            disabled={running}
          />

          {!file ? (
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              disabled={running}
              data-testid="dap-pick-file-btn"
              className="mt-1 flex w-full items-center justify-center gap-2 rounded-md border-2 border-dashed border-cyan-300 bg-white px-3 py-6 text-sm text-slate-700 hover:border-cyan-500 hover:bg-cyan-50 transition-colors"
            >
              <Upload className="h-4 w-4 text-cyan-700" />
              Click to choose a PDF or image from your computer
            </button>
          ) : (
            <div className="mt-1 flex items-center justify-between rounded-md border border-slate-300 bg-white px-3 py-2 text-sm" data-testid="dap-file-chosen">
              <div className="flex items-center gap-2 min-w-0">
                <FileText className="h-4 w-4 shrink-0 text-slate-500" />
                <span className="truncate" title={file.name}>{file.name}</span>
                <span className="text-xs text-slate-400 shrink-0">
                  {(file.size / 1024).toFixed(0)} KB
                </span>
              </div>
              <button
                type="button"
                onClick={reset}
                disabled={running}
                className="ml-2 text-slate-400 hover:text-slate-700"
                data-testid="dap-clear-file"
                aria-label="Remove file"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          )}

          {file && (
            <div className="mt-2 flex items-center gap-2 text-xs">
              {toolLabel ? (
                <>
                  <span className="rounded-full bg-emerald-100 text-emerald-800 px-2 py-0.5 font-medium" data-testid="dap-detected-tool">
                    Detected: {toolLabel}
                  </span>
                  <span className="text-slate-500">— ready to analyze</span>
                </>
              ) : (
                <span className="flex items-center gap-1 text-amber-700" data-testid="dap-detect-failed">
                  <AlertCircle className="h-3 w-3" />
                  Couldn&apos;t auto-detect type. Rename to include &quot;tax-return&quot;/&quot;1120&quot;/&quot;1040&quot;/&quot;P&amp;L&quot; or &quot;bank-statement&quot;/&quot;deposits&quot;.
                </span>
              )}
            </div>
          )}
        </div>

        <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer select-none" data-testid="dap-save-toggle-label">
          <input
            type="checkbox"
            checked={saveToApp}
            onChange={(e) => setSaveToApp(e.target.checked)}
            disabled={running}
            className="h-4 w-4 rounded border-slate-300 text-cyan-700 focus:ring-cyan-500"
            data-testid="dap-save-toggle"
          />
          Also save this file to the application&apos;s documents
        </label>

        <div className="flex flex-wrap gap-2">
          <Button
            onClick={handleRun}
            disabled={!file || !inferred || running}
            className="bg-cyan-700 hover:bg-cyan-800 text-white"
            data-testid="dap-run-btn"
          >
            {running ? (
              <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Analyzing...</>
            ) : (
              <><Sparkles className="h-4 w-4 mr-1.5" /> Upload &amp; Analyze</>
            )}
          </Button>
        </div>

        {feedback && (
          <div
            className={`flex items-start gap-2 p-2.5 rounded-md text-xs ${
              feedback.type === 'ok'
                ? 'bg-emerald-50 border border-emerald-200 text-emerald-900'
                : 'bg-red-50 border border-red-200 text-red-800'
            }`}
            data-testid={feedback.type === 'ok' ? 'dap-feedback-ok' : 'dap-feedback-err'}
          >
            {feedback.type === 'ok' ? <CheckCircle className="h-3.5 w-3.5 mt-0.5 shrink-0" /> : <AlertCircle className="h-3.5 w-3.5 mt-0.5 shrink-0" />}
            <span>{feedback.text}</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default DocumentAnalysisPanel;
